import React, { useRef, useEffect } from 'react';
import { FiChevronRight, FiChevronLeft } from 'react-icons/fi';

const ContentRow = ({ 
  title, 
  items, 
  IMG_URL, 
  onItemClick,
  showRecentIndicator = false,
  showRank = false,
  showDateIndicator = false, // Add this prop for Korean dramas
  isKoreanDramas = false // Add this prop to identify Korean drama rows
}) => {
  const rowRef = useRef(null);
  
  

  const scroll = (direction) => {
    if (rowRef.current) {
      const scrollAmount = direction === 'left' ? -window.innerWidth * 0.7 : window.innerWidth * 0.7;
      rowRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Function to format episode badge text
  const getEpisodeBadgeText = (item) => {
    // Check if this is a Korean drama and if it's a season finale
    if (isKoreanDramas && item.episodeInfo && item.episodeInfo.isSeasonFinale) {
      return 'Ended';
    }
    
    if (item.episodeInfo) {
      const { season, number, name } = item.episodeInfo;
      if (season && number) {
        return `S${season} E${number}`;
      } else if (number) {
        return `Ep ${number}`;
      }
    }
    return 'Latest';
  };

  // Helper function to get Philippines date in YYYY-MM-DD format (matching your koreanDrama.js)
  const getPhilippinesDate = (daysOffset = 0) => {
    const now = new Date();
    const phtDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Manila' }));
    phtDate.setDate(phtDate.getDate() + daysOffset);
    
    const year = phtDate.getFullYear();
    const month = String(phtDate.getMonth() + 1).padStart(2, '0');
    const day = String(phtDate.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Function to get badge color and check if it's a recent episode (within last 2 days)
  const getBadgeInfo = (item) => {
    if (!item.fetchDate) return { color: 'bg-blue-600', isRecent: false, isToday: false };
    
    // Get today's date and yesterday's date in Philippines timezone
    const todayString = getPhilippinesDate();
    const yesterdayString = getPhilippinesDate(-1); // -1 day offset
    
    // Handle different fetchDate formats
    let fetchDateString = item.fetchDate;
    if (fetchDateString instanceof Date) {
      const fetchDate = new Date(fetchDateString);
      fetchDateString = fetchDate.getFullYear() + '-' + 
                       String(fetchDate.getMonth() + 1).padStart(2, '0') + '-' + 
                       String(fetchDate.getDate()).padStart(2, '0');
    } else if (typeof fetchDateString === 'string') {
      // If it's already a string, extract just the date part (YYYY-MM-DD)
      fetchDateString = fetchDateString.split('T')[0];
    }
    
    const isToday = fetchDateString === todayString;
    const isYesterday = fetchDateString === yesterdayString;
    const isRecent = isToday || isYesterday;
    
    // Special handling for Korean dramas that have ended
    if (isKoreanDramas && item.episodeInfo && item.episodeInfo.isSeasonFinale) {
      return { 
        color: 'bg-gray-600', // Different color for ended shows
        isRecent: false, 
        isToday: false,
        isEnded: true
      };
    }
    
    if (isRecent) {
      return { 
        color: 'bg-red-600', 
        isRecent: true, 
        isToday,
        dayLabel: isToday ? 'Today' : 'Yesterday'
      };
    } else {
      return { 
        color: 'bg-blue-600', 
        isRecent: false, 
        isToday: false 
      };
    }
  };

  return (
    <section className="mb-8 relative">
      <div className="flex justify-between items-center mb-4 px-4 md:px-12">
        <h2 className="text-xl md:text-2xl font-bold">{title}</h2>
      </div>
      <div className="relative group">
        <button 
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-2"
        >
          <FiChevronLeft className="w-6 h-6" />
        </button>
        <div 
          ref={rowRef}
          className="flex overflow-x-auto overflow-y-hidden space-x-4 py-2 scrollbar-hide px-4 md:px-12"
          style={{ height: showRank ? '320px' : 'auto' }}
        >
          {items.map((item, index) => {
            return showRank ? (
              <div 
                key={item.id}
                className="flex-shrink-0 flex items-center relative"
                style={{ height: '288px' }}
              >
                <div 
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-[18rem] font-black leading-none text-neutral-800 z-0 flex items-center justify-center select-none pointer-events-none"
                  style={{
                    WebkitTextStroke: '3px white', 
                    textStroke: '3px white',
                    width: '100px',
                    height: '288px'
                  }}
                >
                  {index + 1}
                </div>
                <div 
                  className="relative rounded-md overflow-hidden transition-transform duration-300 hover:scale-105 cursor-pointer w-48 ml-20 z-10 group/poster"
                  onClick={() => onItemClick(item, false)}
                  style={{ height: '288px' }}
                >
                  <img 
                    src={`${IMG_URL}${item.poster_path}`}
                    alt={item.title || item.name}
                    className="w-full h-full object-cover rounded-md"
                    loading="eager"
                  />
                </div>
              </div>
            ) : (
              <div 
                key={item.id || item.tmdb_id} 
                className="flex-shrink-0 relative rounded-md overflow-hidden transition-transform duration-300 hover:scale-105 cursor-pointer w-48"
                onClick={() => onItemClick(item, false)}
              >
                <img 
                  src={`${IMG_URL}${item.poster_path}`}
                  alt={item.title || item.name}
                  className="w-full h-auto object-cover rounded-md"
                  loading="eager"
                />

                {/* Show recent indicator badge */}
                {showRecentIndicator && (
                  <div className="absolute top-2 left-2 bg-red-600 text-xs px-2 py-1 rounded-md">
                    {item.media_type === 'tv' && item.currentSeason && item.currentEpisode 
                      ? `S${item.currentSeason} E${item.currentEpisode}` 
                      : 'Recent'}
                  </div>
                )}

                {/* Show date/episode indicator for Korean dramas */}
                {showDateIndicator && (
                  <div className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-md text-white font-medium ${getBadgeInfo(item).color}`}>
                    {getBadgeInfo(item).isRecent ? `New ${getEpisodeBadgeText(item)}` : getEpisodeBadgeText(item)}
                  </div>
                )}

              </div>
            );
          })}
        </div>
        <button 
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mr-2"
        >
          <FiChevronRight className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
};

export default ContentRow;
